import MUIDataTable from "mui-datatables";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import React, { useState, useEffect } from "react";

import LoadingOverlay from "./components/LoadingOverlay";
import {
    createCustomerNameColumn,
    createDateColumn,
    createBasicColumn,
    createNumericColumn,
    createNumericCurrencyColumn,
} from "./components/TableColumnConfig";

// Helper to update URL with customer ID parameter
const updateUrlWithCustomer = (customerId, customerName) => {
    const url = new URL(window.location);
    if (customerId) {
        url.searchParams.set("id", customerId);
    } else {
        url.searchParams.delete("id");
    }
    window.history.pushState({ customerId, customerName }, "", url);
};

const CustomersTab = (props) => {
    const [customersTableData, setCustomersTableData] = useState();
    const [productsList, setProductsList] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [filteredData, setFilteredData] = useState();

    // Check for id URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const initialCustomerId = urlParams.get("id");

    // Navigate to customer and update URL
    const navigateToCustomer = (id, name) => {
        updateUrlWithCustomer(id, name);
        props.setSelected({ id, name });
        props.setSelectedTab(0);
    };

    useEffect(() => {
        if (props.data) {
            let data = props.data;

            // Check for empty data and skip processing
            if (data.length === 0) return;

            data.sort((a, b) => a.name.localeCompare(b.name));

            // Check for undefined values and replace
            data = data.map((item) => {
                // Map name to customer_name to match the column config
                item.customer_name = item.name;
                item.netsuite_id = item.id;

                // Format total_arr as currency with thousands separator
                if (typeof item.total_arr === "number") {
                    item.total_arr = `$${item.total_arr.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                    })}`;
                }

                Object.keys(item).forEach((key) => {
                    // Keep original product count logic unchanged
                    if (item.products && item.products !== "" && item.products !== "-") {
                        item["products_count"] = item.products.split(",").length;
                    } else {
                        item["products_count"] = 0;
                    }

                    if (!item.country && !item.industry) item["country"] = item["industry"] = "-";

                    if (item[key] === undefined || item[key] === null) item[key] = "-";
                });

                // Add display formatting for products after all other processing
                if (item.products && item.products !== "" && item.products !== "-") {
                    const productsArray = item.products.split(",").map((p) => p.trim());
                    const same_product_counts = {};

                    productsArray.forEach((product) => {
                        same_product_counts[product] = (same_product_counts[product] || 0) + 1;
                    });

                    item.products_display = Object.entries(same_product_counts)
                        .map(([product, count]) => (count > 1 ? `${product} (${count})` : product))
                        .join(", ");
                }

                return item;
            });

            // Extract unique products for dropdown
            const allProducts = new Set();
            data.forEach((item) => {
                if (item.products && item.products !== "-") {
                    const productsArray = item.products.split(",").map((p) => p.trim());
                    productsArray.forEach((product) => {
                        allProducts.add(product);
                    });
                }
            });

            const productOptions = Array.from(allProducts)
                .sort()
                .map((product) => ({
                    label: product,
                    value: product,
                }));

            setProductsList(productOptions);
            setCustomersTableData(data);

            // Apply initial filtering to only show active customers
            const activeOnly = data.filter(
                (item) => item.isinactive === "F" || item.isinactive === false
            );
            setFilteredData(activeOnly);

            // If initial URL has customer id, navigate to that customer
            if (initialCustomerId) {
                const customer = data.find((c) => String(c.id) === String(initialCustomerId));
                if (customer) {
                    navigateToCustomer(customer.id, customer.name);
                }
            }
        }
    }, [props.data]);

    // Filter data when filters change
    useEffect(() => {
        if (customersTableData) {
            // Always filter out inactive customers
            let filtered = customersTableData.filter(
                (item) => item.isinactive === "F" || item.isinactive === false
            );

            // Filter by selected product (only if a value is selected)
            if (selectedProduct) {
                filtered = filtered.filter((item) => {
                    if (item.products && item.products !== "-") {
                        const productsArray = item.products.split(",").map((p) => p.trim());
                        return productsArray.includes(selectedProduct);
                    }
                    return false;
                });
            }

            setFilteredData(filtered);
        }
    }, [customersTableData, selectedProduct]);

    const columns = [
        createCustomerNameColumn(filteredData, (item) =>
            navigateToCustomer(item.netsuite_id, item.customer_name)
        ),
        createNumericCurrencyColumn("total_arr", "Total ARR", undefined, filteredData),
        createNumericColumn("products_count", "Product Count"),
        {
            ...createBasicColumn("products_display", "Products"),
            options: {
                ...createBasicColumn("products_display", "Products").options,
                filter: false,
            },
        },
        createDateColumn("next_renewal_date", "Next Renewal", filteredData),
        createBasicColumn("accountManagerName", "Account Manager"),
        createBasicColumn("customerSuccessManagerName", "CS Manager"),
        createNumericColumn("open_tickets", "Open Tickets"),
        createNumericColumn("tickets_12mo", "Tickets 12 MO"),
        createBasicColumn("implementation_status", "Implementation"),
        createBasicColumn("billing_status", "Billing Status"),
        // createBasicColumn("industry", "INDUSTRY"),
        // createBasicColumn("country", "COUNTRY"),
    ];

    const options = {
        responsive: "standard",
        selectableRows: "none",
        searchAlwaysOpen: true,
        storageKey: "customersTable",
        filter: true,
        sort: true,
        rowsPerPage: 250,
        rowsPerPageOptions: [50, 100, 250, 500, 1000],
        print: false,
        download: false,
        search: true,
        columnOrder: null,
        fixedHeader: false,
    };

    return (
        <div className="flex w-full flex-col bg-white pt-4">
            <Card className="mb-0 bg-white">
                <div className="flex flex-row items-center justify-end gap-4 py-1">
                    <div className="flex flex-row items-center">
                        <Dropdown
                            id="product-filter"
                            value={selectedProduct}
                            options={productsList}
                            onChange={(e) => setSelectedProduct(e.value)}
                            placeholder="Filter By Product"
                            className="w-72"
                            showClear
                            style={{
                                backgroundColor: "#98D2F3",
                                borderRadius: "0.5rem",
                                border: "none",
                                padding: "0.5rem",
                                color: "white",
                                fontWeight: "bold",
                                paddingRight: "0.75rem",
                                marginRight: "4px",
                            }}
                            panelStyle={{
                                backgroundColor: "white",
                                borderColor: "#98D2F3",
                                borderWidth: "1px",
                                borderStyle: "solid",
                                borderRadius: "0.5rem",
                                marginTop: "0.25rem",
                                paddingLeft: "0.5rem",
                            }}
                        />
                    </div>
                </div>
            </Card>

            {filteredData ? (
                <MUIDataTable
                    data={filteredData}
                    columns={columns}
                    options={options}
                    className="ngShadow"
                />
            ) : (
                <LoadingOverlay text="Loading customer list..." />
            )}
        </div>
    );
};

export default CustomersTab;
